import { styled } from '@mui/material/styles';
import { Box, Paper, Button, Typography } from '@mui/material';

export const DashboardContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: 0,
  minHeight: '100%',
});

export const DashboardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
});

export const Title = styled(Typography)({
  fontWeight: 700,
  fontSize: '24px',
  color: '#2b3445',
});

export const HeaderActions = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
});

export const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: '6px 16px',
  borderRadius: '6px',
  fontWeight: 500,
  fontSize: '14px',
  height: '36px',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}));

export const StatsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '20px',
});

export const ChartGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 2fr',
  gap: '24px',
  '@media (max-width: 960px)': {
    gridTemplateColumns: '1fr',
  },
});

export const CardContainer = styled(Paper)({
  padding: '24px',
  borderRadius: '12px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  border: '0.5px solid #eaeaea',
  '& .recharts-legend-wrapper': {
    paddingTop: '10px !important',
  },
});

export const StatCardWrapper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: '20px',
  borderRadius: '12px',
  boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.03)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  border: '0.5px solid #eaeaea',
  overflow: 'hidden',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  cursor: 'default',
  minHeight: '110px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.08)',
  },
}));

export const WaveBackground = styled('svg')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: 'auto',
  zIndex: 1,
});

export const StatContentWrapper = styled(Box)({
  position: 'relative',
  zIndex: 2,
});

export const StatTitle = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: '8px',
});

export const StatValue = styled(Typography)<{
  colorType?: 'default' | 'error';
}>(({ colorType }) => ({
  fontSize: '24px',
  fontWeight: 700,
  color: colorType === 'error' ? '#ef4444' : '#1f2937',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

export const PercentageBadge = styled('span')<{ isPositive?: boolean }>(
  ({ isPositive }) => ({
    fontSize: '14px',
    fontWeight: 600,
    color: isPositive ? '#10b981' : '#ef4444',
  })
);

export const PercentageSubtext = styled('span')({
  fontSize: '12px',
  fontWeight: 400,
  color: '#6b7280',
});

export const ChartTitle = styled(Typography)<{ noMargin?: boolean }>(
  ({ noMargin }) => ({
    fontWeight: 600,
    fontSize: '16px',
    color: '#374151',
    marginBottom: noMargin ? '8px' : '20px',
  })
);

export const TableGrid = styled(Box)<{ isReversed?: boolean }>(
  ({ isReversed }) => ({
    display: 'grid',
    gridTemplateColumns: isReversed ? '1fr 1fr' : '1.5fr 1fr',
    gap: '24px',
    '@media (max-width: 1200px)': {
      gridTemplateColumns: '1fr',
    },
  })
);

export const StyledTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
});

export const TableHeader = styled('thead')({
  backgroundColor: '#f8fafc',
});

export const TableRow = styled('tr')({
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#f8fafc',
  },
});

export const StatusBadge = styled('span')<{ status?: string }>(({ status }) => {
  let bgColor = '#f1f5f9';
  let textColor = '#64748b';

  // Simple mapping for common statuses found in enum
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'payment completed':
    case 'approved':
      bgColor = '#ecfdf5';
      textColor = '#10b981';
      break;
    case 'payment pending':
    case 'pending approval':
    case 'new':
      bgColor = '#fff7ed';
      textColor = '#f59e0b';
      break;
    case 'rejected':
      bgColor = '#fef2f2';
      textColor = '#ef4444';
      break;
  }

  return {
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: bgColor,
    color: textColor,
    display: 'inline-block',
  };
});

export const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  minHeight: '400px',
});

export const StatUnit = styled('span')({
  fontSize: '14px',
  fontWeight: 500,
  color: '#6b7280',
});

export const ChartBox = styled(Box)({
  flex: 1,
  minHeight: '250px',
});

export const TableCard = styled(CardContainer)({
  padding: 0,
  overflow: 'hidden',
});

export const TableHeaderWrapper = styled(Box)({
  padding: '24px 24px 8px 24px',
});

export const ScrollableTableContainer = styled(Box)({
  overflowX: 'auto',
});

export const TableHeaderCell = styled('th')<{
  align?: 'left' | 'center' | 'right';
}>(({ align = 'left' }) => ({
  padding: '12px 16px',
  textAlign: align,
  fontSize: '12px',
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
}));

export const TableDataCell = styled('td')<{
  align?: 'left' | 'center' | 'right';
  fontWeight?: number | string;
  colorType?: 'default' | 'error' | 'success';
  isMuted?: boolean;
  padding?: string;
}>(({ align = 'left', fontWeight, colorType, isMuted, padding }) => {
  let color = isMuted ? '#6b7280' : '#334155';
  if (colorType === 'error') color = '#ef4444';
  if (colorType === 'success') color = '#10b981';

  return {
    padding: padding || '16px',
    fontSize: '14px',
    color: color,
    borderBottom: '1px solid #f1f5f9',
    textAlign: align,
    fontWeight: fontWeight || 'inherit',
  };
});

export const TooltipWrapper = styled(Paper)({
  padding: '12px 16px',
  borderRadius: '12px',
  border: 'none',
  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
  backgroundColor: '#fff',
});

export const TooltipLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#1f2937',
  marginBottom: '8px',
});

export const TooltipItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '13px',
  color: '#4b5563',
  '& .value': {
    fontWeight: 600,
    color: '#111827',
  },
});

export const TooltipMarker = styled('div')<{ bgColor: string }>(
  ({ bgColor }) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: bgColor,
  })
);
